import PropTypes from 'prop-types';
import { useMemo, useEffect, useReducer, useCallback } from 'react';

import { paths } from 'src/routes/paths';

import { supabase } from './lib';
import { AuthContext } from './auth-context';
// 유저 정보 저장용 초기 상태 및 리듀서
const initialState = {
  user: null,
  loading: true,
  authInitialized: false,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === 'AUTH_STATE_CHANGED') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'AUTH_INITIALIZED') {
    return {
      ...state,
      authInitialized: true,
    };
  }

  return state;
};

// ------------------- ---------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error || !session) {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
        throw error;
      }
      const userData = await getUserRow();
      if (userData?.role !== 'Admin') {
        await logout();
        throw new Error('관리자만 접근 가능합니다.');
      }

      dispatch({
        type: 'INITIAL',
        payload: {
          user: {
            ...userData,
            avatarURL: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/avatar/${
              userData?.user_id
            }.jpg`,
          },
        },
      });
      dispatch({ type: 'AUTH_INITIALIZED' });
    } catch (error) {
      console.error('err at line 96');
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
    dispatch({ type: 'AUTH_INITIALIZED' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    supabase.auth.onAuthStateChange(() => {
      initialize();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 계정 및 인증 관련 함수들
  const login = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      dispatch({
        type: 'LOGIN',
        payload: {
          user: null,
        },
      });
      console.error(error);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = useCallback(async (email, password, user_name, role) => {
    try {
      const { data: userData, error: userError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role,
            user_name,
          },
        },
      });
      if (userError) throw userError;
      return userData;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error(error);
      throw error;
    }

    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${paths.auth.supabase.newPassword}`,
    });

    if (error) {
      console.error(error);
      throw error;
    }
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      console.error(error);
      throw error;
    }
  }, []);

  const getUserRow = useCallback(async (userId) => {
    const { data, error } = await supabase.rpc('new_get_user_row');
    if (error) throw error;
    return data;
  }, []);

  const forgotEmail = useCallback(async (email, captcha) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-captcha`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ email, captcha }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error);
    }

    const data = await response.json();
    return data;
  }, []);

  // ----------------------------------------------------------------------
  // 공통 함수들
  // getContextQuestionData : 문제를 가져오는 함수.
  const getContextQuestionData = useCallback(async (contextId) => {
    const { data, error } = await supabase.rpc('adm_new_get_context_data', {
      context_id_input: contextId,
    });
    if (error) throw error;

    return data;
  }, []);

  // getSolveData : 해당 워크북 또는 클리닉의 문제 풀이 데이터를 가져옴.
  const getSolveData = useCallback(
    async (progressOrAssignmentId, solveType) => {
      const { data, error } = await supabase.rpc('new_get_solve_data', {
        solvetype: solveType,
        progress_or_assignment_id: progressOrAssignmentId,
      });

      if (error) throw error;
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // getIndividualHistory : 특정 문제의 풀이 기록을 가져옴.
  const getIndividualHistory = useCallback(
    async (student_question_solve_history_id) => {
      const { data, error } = await supabase.rpc('new_get_individual_history', {
        individual_history_id: student_question_solve_history_id,
      });
      if (error) throw error;
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 관리자 전용 함수들
  const searchContexts = useCallback(
    async (searchParams) => {
      const { data, error } = await supabase.rpc('admin_search_contexts', searchParams);
      if (error) throw error;
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const searchContextsORM = useCallback(
    async (searchParams, hideChecked, currentPage, rowsPerPage) => {
      console.log('searchParams', searchParams);
      const query = supabase
        .from('db_context')
        .select('*', { count: 'exact' })
        .in('grade', searchParams.p_grade)
        .in('month', searchParams.p_month)
        .in('year', searchParams.p_year)
        .in('agency', searchParams.p_agency)
        .ilike('context_text', `%${searchParams.p_query}%`)
        .order('year', { ascending: searchParams.p_year_sort === 'asc' })
        .order('month', { ascending: searchParams.p_month_sort === 'asc' })
        .order('grade', { ascending: searchParams.p_grade_sort === 'asc' })
        .order('context_id', { ascending: true })
        .range(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage - 1);

      if (hideChecked) {
        query.eq('checked', false);
      }

      const { data, error, count } = await query;
      console.log('searchContextsORM -> data', data);

      if (error) throw error;

      return { data, count };
    },
    []
  );

  const searchContextList = useCallback(async (searchParams) => {
    const { data, count, error } = await supabase
      .from('db_context')
      .select(
        `
        context_id, grade, difficulty, topic, category, source, year, month, agency, agency_note, db_question(question_number)
      `,
        { count: 'exact' }
      )
      .eq('grade', searchParams.grade)
      .eq('month', searchParams.month)
      .eq('year', searchParams.year)
      .eq('agency', searchParams.agency)
      .order('context_id', { ascending: true });

    if (error) throw error;
    const formattedData = data.map((context) => ({
      ...context,
      question_numbers: context.db_question.map((q) => q.question_number).sort((a, b) => a - b),
    }));

    formattedData.sort((a, b) => a.question_numbers[0] - b.question_numbers[0]);

    return { data: formattedData, count };
  }, []);

  const countContexts = useCallback(async (searchParams) => {
    const { data, error } = await supabase.rpc('admin_count_contexts', searchParams);
    if (error) throw error;
    return data;
  }, []);

  const countContextsORM = useCallback(async (searchParams, hideChecked) => {
    const query = supabase
      .from('db_context')
      .select('*', { count: 'exact', head: true })
      .in('grade', searchParams.p_grade)
      .in('month', searchParams.p_month)
      .in('year', searchParams.p_year)
      .in('agency', searchParams.p_agency)
      .ilike('context_text', `%${searchParams.p_query}%`);

    if (hideChecked) {
      query.eq('checked', false);
    }

    const { count, error } = await query;
    if (error) throw error;
    console.log('countContextsORM -> data', count);
    return count;
  }, []);

  const upsertQuestions = useCallback(
    async (p_input_data) => {
      const { data, error } = await supabase.rpc('admin_upsert_questions', { p_input_data });
      if (error) throw error;
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getFilterValues = useCallback(async () => {
    const { data, error } = await supabase.rpc('admin_get_filter_values');
    if (error) throw error;
    return data?.[0];
  }, []);

  const generateNewQuestion = useCallback(async (context, modelName, temperature, topP, qType) => {
    const { data, error } = await supabase.functions.invoke('request-qgen', {
      body: { context, modelName, temperature, topP, qType },
    });
    if (error) throw new Error('서버와 통신 중 오류가 발생하였습니다.');
    return data;
  }, []);

  const generateNewCurriculum = useCallback(async (curriculumData) => {
    console.log('newCurriculum -> curriculumData', curriculumData);
    const { data, error } = await supabase.rpc('adm_new_curriculum', curriculumData);
    if (error) throw error;
    return data;
  }, []);

  const getCustomPlanList = useCallback(async () => {
    const { data, error } = await supabase.from('custom_payment_plans').select('*');
    if (error) throw error;
    return data;
  }, []);
  const getCustomPlanPaymentList = useCallback(async () => {
    const { data, error } = await supabase.from('custom_payment_transactions').select('*');
    if (error) throw error;
    return data;
  }, []);

  const addCustomPlan = useCallback(async (plan) => {
    const { data, error } = await supabase.from('custom_payment_plans').insert(plan);
    if (error) throw error;
    return data;
  }, []);
  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user?.user_id ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: {
        ...state.user,
      },
      method: 'supabase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      authInitialized: state.authInitialized,
      //
      login,
      register,
      logout,
      forgotPassword,
      updatePassword,
      forgotEmail,
      // 공통 함수
      getContextQuestionData,
      getSolveData,
      getIndividualHistory,
      // 관리자 전용 함수들
      searchContexts,
      countContexts,
      upsertQuestions,
      getFilterValues,
      searchContextsORM,
      countContextsORM,
      generateNewQuestion,
      searchContextList,
      generateNewCurriculum,
      getCustomPlanList,
      getCustomPlanPaymentList,
      addCustomPlan,
    }),
    [
      forgotPassword,
      login,
      logout,
      updatePassword,
      register,
      forgotEmail,
      // 공통 함수
      getContextQuestionData,
      getSolveData,
      getIndividualHistory,

      // 관리자 전용 함수들
      searchContexts,
      countContexts,
      upsertQuestions,
      getFilterValues,
      searchContextsORM,
      countContextsORM,
      generateNewQuestion,
      searchContextList,
      generateNewCurriculum,
      getCustomPlanList,
      getCustomPlanPaymentList,
      addCustomPlan,
      //
      state.user,
      status,
      state.authInitialized,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
